import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Grid, IconButton, TextField, Typography } from "@mui/material";
import React, { useCallback } from "react";

import { WorkerTypeHours } from "../../../../store/gat-admin-slice-types";
import { useWeeklyHourLimitController } from "../controllers/use-weekly-hour-limit-controller";

interface WorkerTypeHourLimitProps {
	workerTypeHour: WorkerTypeHours;
}

const MAX_HOURS = 168;

const getInputSx = (isFocused: boolean) => ({
	width: "120px",
	"& .MuiInputBase-input": {
		p: 1,
		background: isFocused ? "#FFF" : "#F5F5F5",
	},
});

const WorkerTypeHourLimit = ({ workerTypeHour }: WorkerTypeHourLimitProps) => {
	const [
		{ workerTypeLimit, isFocused, validationError, rowRef },
		{
			setIsFocused,
			handleBlur,
			handleHoursChange,
			handleSave,
			handleCancel,
			handleActionMouseDown,
		},
	] = useWeeklyHourLimitController(workerTypeHour);

	const handleFocus = useCallback(() => setIsFocused(true), [setIsFocused]);

	const handleMinChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) =>
			handleHoursChange(parseInt(event.target.value), "min"),
		[handleHoursChange]
	);

	const handleMaxChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) =>
			handleHoursChange(parseInt(event.target.value), "max"),
		[handleHoursChange]
	);

	if (!workerTypeLimit) return null;

	const maxHours = workerTypeLimit.maxHours ?? MAX_HOURS;
	const maxHelperText =
		maxHours >= MAX_HOURS && isFocused ? "Max. limit is 168" : " ";

	return (
		<Grid
			container
			ref={rowRef}
			onBlur={handleBlur}
			sx={{
				display: "flex",
				alignItems: "flex-start",
				p: 2,
			}}
		>
			<Grid item xs={6}>
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontSize: "16px",
						color: "rgba(0, 0, 0, 0.87)",
					}}
				>
					{workerTypeLimit.workerTypeName}
					{workerTypeLimit.workerSubTypeName &&
						` - ${workerTypeLimit.workerSubTypeName}`}
				</Typography>
			</Grid>
			<Grid item xs={3}>
				<TextField
					id={`number-of-minimum-hours-${workerTypeLimit.workerTypeId}-${workerTypeLimit.workerSubTypeId}`}
					variant='filled'
					type='number'
					onFocus={handleFocus}
					sx={getInputSx(isFocused)}
					onChange={handleMinChange}
					value={workerTypeLimit.minHours ?? 0}
					error={!!validationError}
					helperText={validationError || " "}
					inputProps={{
						min: 0,
						max: MAX_HOURS,
						"aria-label": "Minimum hours",
					}}
				/>
			</Grid>
			<Grid item xs={3} sx={{ display: "flex", alignItems: "flex-start" }}>
				<TextField
					id={`number-of-maximum-hours-${workerTypeLimit.workerTypeId}-${workerTypeLimit.workerSubTypeId}`}
					variant='filled'
					type='number'
					onFocus={handleFocus}
					sx={getInputSx(isFocused)}
					onChange={handleMaxChange}
					value={maxHours}
					helperText={maxHelperText}
					inputProps={{
						min: 0,
						max: MAX_HOURS,
						"aria-label": "Maximum hours",
					}}
				/>
				{isFocused && (
					<Box sx={{ display: "flex", p: 0.5 }}>
						<IconButton
							size='small'
							aria-label='Save'
							onMouseDown={handleActionMouseDown}
							onClick={handleSave}
							sx={{ mx: 1, mb: 1 }}
						>
							<CheckIcon />
						</IconButton>
						<IconButton
							size='small'
							aria-label='Cancel'
							onMouseDown={handleActionMouseDown}
							onClick={handleCancel}
							sx={{ mx: 1, mb: 1 }}
						>
							<CloseIcon />
						</IconButton>
					</Box>
				)}
			</Grid>
		</Grid>
	);
};

export default WorkerTypeHourLimit;
